<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useAuthStore } from '@yuksalish/auth'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { useI18n } from 'vue-i18n'
import ReferenceItemCreateModal from './ReferenceItemCreateModal.vue'
import type { EquipmentReference, ReferenceItem } from '../types'

const props = defineProps<{
  open: boolean
  title: string
  equipment: EquipmentReference[]
}>()

const emit = defineEmits<{
  close: []
  select: [equipment: EquipmentReference]
}>()

const auth = useAuthStore()
const { t } = useI18n()
const search = ref('')
const page = ref(1)
const createOpen = ref(false)
const pageSize = 8
const canCreate = computed(() => auth.user?.permissions.includes('products.update') ?? false)
const filteredEquipment = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  if (!query) return props.equipment
  return props.equipment.filter((item) =>
    [item.name, item.code]
      .some((value) => String(value ?? '').toLocaleLowerCase().includes(query)),
  )
})
const totalPages = computed(() => Math.max(1, Math.ceil(filteredEquipment.value.length / pageSize)))
const visibleEquipment = computed(() => {
  const start = (page.value - 1) * pageSize
  return filteredEquipment.value.slice(start, start + pageSize)
})
const visibleOptions = computed<SmartPickerOption[]>(() =>
  visibleEquipment.value.map((item) => ({
    id: item.id,
    title: item.name,
    subtitle: item.code || t('engineering.passportPicker.noCode'),
    meta: item.base_amount ? `${item.base_amount} needles · #${item.id}` : `#${item.id}`,
  })),
)

watch(() => props.open, (open) => {
  if (open) {
    search.value = ''
    page.value = 1
  }
}, { immediate: true })

watch(search, () => {
  if (!props.open) return
  page.value = 1
})

function selectOption(option: SmartPickerOption): void {
  const equipment = visibleEquipment.value.find((entry) => entry.id === Number(option.id))
  if (equipment) emit('select', equipment)
}

function handleCreated(equipment: ReferenceItem): void {
  createOpen.value = false
  emit('select', equipment as EquipmentReference)
}
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title"
    :eyebrow="$t('engineering.passportPicker.equipmentRepository')"
    :search-label="$t('engineering.passportPicker.searchMachines')"
    :search-placeholder="$t('engineering.passportPicker.nameOrCode')"
    :search-value="search"
    :loading="false"
    error-message=""
    :empty-message="$t('engineering.passportPicker.noMachines')"
    :items="visibleOptions"
    :page="page"
    :total-pages="totalPages"
    :page-label="$t('engineering.passportPicker.page', { count: filteredEquipment.length, page, total: totalPages })"
    :close-aria-label="$t('engineering.passportPicker.closeMachine')"
    :retry-label="$t('engineering.passportPicker.retry')"
    :previous-label="$t('engineering.passportPicker.previous')"
    :next-label="$t('engineering.passportPicker.next')"
    :cancel-label="$t('engineering.passportPicker.cancel')"
    @close="emit('close')"
    @previous="page--"
    @next="page++"
    @select="selectOption"
    @update:search-value="search = $event"
  >
    <template #helper>
      <div class="picker-helper">
        <div>
          <strong>{{ $t('engineering.passportPicker.needMachine') }}</strong>
          <p>{{ $t('engineering.passportPicker.createAutoSelect') }}</p>
        </div>
        <button type="button" :disabled="!canCreate" @click="createOpen = true">
          {{ $t('engineering.passportPicker.addNew') }}
        </button>
      </div>
    </template>
  </SmartPickerModal>
  <ReferenceItemCreateModal
    :open="createOpen"
    :title="$t('engineering.passportPicker.createMachine')"
    type="equipment"
    :base-amount-label="$t('engineering.passportForm.needleCount')"
    @close="createOpen = false"
    @created="handleCreated"
  />
</template>

<style scoped>
.picker-helper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #d7dfdc;
  background: #f7fbff;
}

.picker-helper strong {
  color: #173454;
  font-size: 13px;
}

.picker-helper p {
  margin: 0;
  color: #66726e;
  font-size: 12px;
  line-height: 1.4;
}

.picker-helper button {
  padding: 8px 12px;
  border: 1px solid #b9c5c1;
  color: #285b9a;
  background: #fff;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.picker-helper button:disabled {
  color: #9aa7a3;
  background: #f3f6f5;
  cursor: default;
}
</style>
